<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  funnelName: { type: String, default: '' },
  stages: { type: Array, default: () => [] },
  cards: { type: Array, default: () => [] },
  filterSummary: { type: String, default: '' },
});

defineEmits(['close']);

const { t } = useI18n();

// 'list' = um card por linha com a etapa em que está
// 'summary' = quantidade e valor por etapa
const mode = ref('list');
const reportRef = ref(null);

const generatedAt = new Date().toLocaleString();

const formatCurrency = value => {
  const numeric = Number(value) || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numeric);
};

const statusLabel = status => {
  if (status === 'won') return t('CRM.DETAIL.STATUS_WON');
  if (status === 'lost') return t('CRM.DETAIL.STATUS_LOST');
  return t('CRM.DETAIL.STATUS_OPEN');
};

const orderedStages = computed(() =>
  [...props.stages].sort((a, b) => a.position - b.position)
);

// Cards agrupados na ordem das etapas — a coluna "Etapa" responde
// "em qual etapa está cada card" sem precisar quebrar em várias tabelas.
const rows = computed(() => {
  const out = [];
  orderedStages.value.forEach(stage => {
    props.cards
      .filter(c => c.funnel_stage_id === stage.id)
      .sort((a, b) => a.position - b.position)
      .forEach(card => out.push({ card, stage }));
  });
  return out;
});

const summaryRows = computed(() =>
  orderedStages.value.map(stage => {
    const stageCards = props.cards.filter(c => c.funnel_stage_id === stage.id);
    return {
      stage,
      count: stageCards.length,
      total: stageCards.reduce((sum, c) => sum + (Number(c.value) || 0), 0),
    };
  })
);

const totalValue = computed(() =>
  props.cards.reduce((sum, c) => sum + (Number(c.value) || 0), 0)
);

const PRINT_STYLES = `
*{box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#111;margin:0;font-size:12px}
h1{font-size:18px;margin:0 0 6px}
.meta{color:#555;font-size:11px;margin:0 0 2px}
table{width:100%;border-collapse:collapse;margin-top:12px}
th,td{border:1px solid #ccc;padding:5px 7px;text-align:left;vertical-align:top}
th{background:#f2f2f2;font-weight:600}
tfoot td{font-weight:700;background:#fafafa}
.num{text-align:right;white-space:nowrap}
thead{display:table-header-group}
tr{page-break-inside:avoid}
@page{margin:12mm}
`;

// Imprime num iframe isolado: o conteúdo já foi escapado pelo Vue e o
// documento gerado não compartilha CSS com o app, então não há como
// afetar a tela nem os estilos existentes.
const printReport = () => {
  if (!reportRef.value) return;

  const frame = document.createElement('iframe');
  frame.className = 'fixed bottom-0 right-0 w-0 h-0 border-0';
  document.body.appendChild(frame);

  const frameDoc = frame.contentWindow.document;
  frameDoc.open();
  frameDoc.write(
    `<!doctype html><html><head><meta charset="utf-8"><style>${PRINT_STYLES}</style></head><body>${reportRef.value.innerHTML}</body></html>`
  );
  frameDoc.close();
  frameDoc.title = props.funnelName || 'CRM';

  frame.contentWindow.focus();
  frame.contentWindow.print();
  setTimeout(() => frame.remove(), 1000);
};
</script>

<template>
  <div class="flex flex-col h-auto max-h-[85vh]">
    <woot-modal-header :header-title="t('CRM.REPORT.TITLE')" />

    <div class="flex items-center gap-2 px-8 pt-1">
      <button
        v-for="tab in [
          { value: 'list', label: t('CRM.REPORT.MODE_LIST') },
          { value: 'summary', label: t('CRM.REPORT.MODE_SUMMARY') },
        ]"
        :key="tab.value"
        type="button"
        class="px-3 py-1 text-sm rounded-md transition-all duration-150"
        :class="
          mode === tab.value
            ? 'bg-n-alpha-2 font-medium text-n-slate-12'
            : 'text-n-slate-10 hover:text-n-slate-12'
        "
        @click="mode = tab.value"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="flex-1 px-8 py-3 overflow-y-auto">
      <div ref="reportRef" class="text-sm text-n-slate-12">
        <h1 class="text-lg font-bold">
          {{ t('CRM.REPORT.HEADING', { funnel: funnelName }) }}
        </h1>
        <p class="meta text-xs text-n-slate-10">
          {{ t('CRM.REPORT.GENERATED_AT', { date: generatedAt }) }}
        </p>
        <p class="meta text-xs text-n-slate-10">
          {{ t('CRM.REPORT.FILTERS', { filters: filterSummary }) }}
        </p>
        <p class="meta text-xs text-n-slate-10">
          {{ t('CRM.REPORT.TOTAL_CARDS', { count: cards.length }) }}
        </p>

        <p v-if="!cards.length" class="mt-4 text-n-slate-10">
          {{ t('CRM.REPORT.EMPTY') }}
        </p>

        <table v-else-if="mode === 'list'">
          <thead>
            <tr>
              <th>{{ t('CRM.REPORT.COL_CARD') }}</th>
              <th>{{ t('CRM.REPORT.COL_CONTACT') }}</th>
              <th>{{ t('CRM.REPORT.COL_STAGE') }}</th>
              <th>{{ t('CRM.REPORT.COL_ASSIGNEE') }}</th>
              <th class="num">{{ t('CRM.REPORT.COL_VALUE') }}</th>
              <th>{{ t('CRM.REPORT.COL_STATUS') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.card.id">
              <td>{{ row.card.title }}</td>
              <td>{{ row.card.contact?.name || '—' }}</td>
              <td>{{ row.stage.name }}</td>
              <td>{{ row.card.assignee?.name || '—' }}</td>
              <td class="num">{{ formatCurrency(row.card.value) }}</td>
              <td>{{ statusLabel(row.card.status) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4">{{ t('CRM.REPORT.TOTAL') }}</td>
              <td class="num">{{ formatCurrency(totalValue) }}</td>
              <td />
            </tr>
          </tfoot>
        </table>

        <table v-else>
          <thead>
            <tr>
              <th>{{ t('CRM.REPORT.COL_STAGE') }}</th>
              <th class="num">{{ t('CRM.REPORT.COL_COUNT') }}</th>
              <th class="num">{{ t('CRM.REPORT.COL_TOTAL_VALUE') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in summaryRows" :key="row.stage.id">
              <td>{{ row.stage.name }}</td>
              <td class="num">{{ row.count }}</td>
              <td class="num">{{ formatCurrency(row.total) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>{{ t('CRM.REPORT.TOTAL') }}</td>
              <td class="num">{{ cards.length }}</td>
              <td class="num">{{ formatCurrency(totalValue) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div class="flex justify-end gap-2 px-8 py-3 border-t border-n-weak">
      <Button
        faded
        slate
        type="button"
        :label="t('CRM.REPORT.CLOSE')"
        @click="$emit('close')"
      />
      <Button
        type="button"
        icon="i-lucide-printer"
        :label="t('CRM.REPORT.PRINT')"
        :disabled="!cards.length"
        @click="printReport"
      />
    </div>
  </div>
</template>
